import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { TrailerTypeEnum } from "types/TrailerType";

import { useFinalizeAndSendQr } from "../_hooks/useFinalizeAndSendQr";
import { useQuoteRequestSelector } from "../_store";
import { QuoteStepIndex } from "../types";
import { LocationStep } from "./_components/location/LocationStep";
import PartnersStep from "./_components/partners/PartnersStep";
import { ReviewStep } from "./_components/review/ReviewStep";
import { ShipmentStep } from "./_components/shipment/ShipmentStep";
import TeamMembersStep from "./_components/team-members/TeamMembersStep";
import { FinalSchema, locationSchema, partnersSchema, shipmentSchema, teamSchema } from "./_schemas";

const schemaMap = {
  [QuoteStepIndex.Location]: locationSchema,
  [QuoteStepIndex.Shipment]: shipmentSchema,
  [QuoteStepIndex.Partners]: partnersSchema,
  [QuoteStepIndex.Team]: teamSchema,
  [QuoteStepIndex.Review]: teamSchema,
};

export const QuoteRequestLTLForm: React.FC = () => {
  const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { finalizeAndSendQr, resetLocalState } = useFinalizeAndSendQr();

  // @ts-expect-error this is fine
  const currentSchema = schemaMap[stepIndex];

  const methods = useForm<FinalSchema>({
    mode: "onSubmit",
    resolver: zodResolver(currentSchema),
    values: {
      pickup: (airQuoteRequest.stops?.[0] as FinalSchema["pickup"]) ?? {},
      delivery: (airQuoteRequest.stops?.[1] as FinalSchema["delivery"]) ?? {},
      deadline_respond_at: airQuoteRequest.deadline_respond_at ?? "",
      trailer_type: airQuoteRequest.load_spec?.trailer_type ?? TrailerTypeEnum.CARGO_VAN,
      goods_value: airQuoteRequest.goods_value ?? undefined,
      is_hazardous: airQuoteRequest.is_hazardous ?? undefined,
      hazardous_goods_details: airQuoteRequest.hazardous_goods_details ?? undefined,
      max_temp: airQuoteRequest.load_spec?.max_temp ?? undefined,
      min_temp: airQuoteRequest.load_spec?.min_temp ?? undefined,
      partners: airQuoteRequest.partners ?? [],
      teammates: airQuoteRequest.teammates,
      ltl_load_spec: airQuoteRequest.load_spec as FinalSchema["ltl_load_spec"],
      note: airQuoteRequest.note ?? undefined,
      reference_number: airQuoteRequest.reference_number ?? undefined,
      internal_reference_number: airQuoteRequest.internal_reference_number ?? undefined,
      hide_internal_ref_till_dispatch: airQuoteRequest.hide_internal_ref_till_dispatch ?? false,
      accessorials: (airQuoteRequest.load_spec?.accessorials as FinalSchema["accessorials"]) ?? [],
    },
  });

  const onSubmit = () => {
    finalizeAndSendQr();
  };

  if (resetLocalState) {
    return null;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <LocationStep onSubmit={onSubmit} />
        <ShipmentStep onSubmit={onSubmit} />
        <PartnersStep onSubmit={onSubmit} />
        <TeamMembersStep onSubmit={onSubmit} />
        <ReviewStep onSubmit={onSubmit} />
      </FormProvider>
    </div>
  );
};
